import { useState } from 'react';
import { UserIcon, BellIcon, KeyIcon, Cog6ToothIcon, CheckIcon } from '@heroicons/react/24/outline';
import toast from 'react-hot-toast';
import { Card, CardHeader, CardTitle, Button, Input } from '../components/ui';

export default function Settings() {
  const [profile, setProfile] = useState({
    name: 'Farm Manager',
    email: 'manager@kenagri.io',
    organization: 'KENAGRO Demo',
  });

  const [notifications, setNotifications] = useState({
    email: true,
    push: true,
    ndviAlerts: true,
    weatherAlerts: true,
    weeklyReport: true,
  });

  const [preferences, setPreferences] = useState({
    satellite: 'sentinel-2',
    maxCloudCover: 20,
    units: 'metric',
  });

  const handleSave = () => {
    toast.success('Settings saved successfully!');
  };

  const Toggle = ({ enabled, onChange }: { enabled: boolean; onChange: () => void }) => (
    <button
      onClick={onChange}
      className={`relative w-12 h-6 rounded-full transition-all duration-200 ${
        enabled ? 'bg-gradient-to-r from-kenagri-500 to-kenagri-600' : 'bg-gray-200'
      }`}
    >
      <span
        className={`absolute top-1 h-4 w-4 bg-white rounded-full shadow-soft transition-all duration-200 ${
          enabled ? 'left-7' : 'left-1'
        }`}
      />
    </button>
  );

  return (
    <div className="p-6 max-w-4xl mx-auto space-y-6">
      {/* Header */}
      <div className="mb-8">
        <h1 className="text-2xl font-bold text-gray-900">Settings</h1>
        <p className="text-gray-500 mt-1">Manage your account and preferences</p>
      </div>

      {/* Profile Section */}
      <Card variant="gradient">
        <CardHeader>
          <div className="flex items-center gap-3">
            <div className="p-2 rounded-xl bg-gradient-to-br from-kenagri-500 to-kenagri-600 shadow-glow-green">
              <UserIcon className="h-5 w-5 text-white" />
            </div>
            <CardTitle>Profile</CardTitle>
          </div>
        </CardHeader>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
          <Input
            label="Name"
            value={profile.name}
            onChange={(e) => setProfile({ ...profile, name: e.target.value })}
            placeholder="Your full name"
          />
          <Input
            label="Email"
            type="email"
            value={profile.email}
            onChange={(e) => setProfile({ ...profile, email: e.target.value })}
            placeholder="your@email.com"
          />
          <div className="md:col-span-2">
            <Input
              label="Organization"
              value={profile.organization}
              onChange={(e) => setProfile({ ...profile, organization: e.target.value })}
              placeholder="Your organization name"
            />
          </div>
        </div>
      </Card>

      {/* Notifications Section */}
      <Card variant="gradient">
        <CardHeader>
          <div className="flex items-center gap-3">
            <div className="p-2 rounded-xl bg-gradient-to-br from-blue-500 to-blue-600 shadow-glow-blue">
              <BellIcon className="h-5 w-5 text-white" />
            </div>
            <CardTitle>Notifications</CardTitle>
          </div>
        </CardHeader>
        <div className="space-y-1">
          {[
            { key: 'email', label: 'Email Notifications', description: 'Receive updates via email' },
            { key: 'push', label: 'Push Notifications', description: 'Browser push notifications' },
            { key: 'ndviAlerts', label: 'NDVI Alerts', description: 'Get notified about vegetation changes' },
            { key: 'weatherAlerts', label: 'Weather Alerts', description: 'Severe weather notifications' },
            { key: 'weeklyReport', label: 'Weekly Report', description: 'Summary of farm analytics' },
          ].map(({ key, label, description }) => (
            <div
              key={key}
              className="flex items-center justify-between p-4 rounded-xl hover:bg-gray-50 transition-colors"
            >
              <div>
                <p className="font-medium text-gray-900">{label}</p>
                <p className="text-sm text-gray-500">{description}</p>
              </div>
              <Toggle
                enabled={notifications[key as keyof typeof notifications]}
                onChange={() => setNotifications({ ...notifications, [key]: !notifications[key as keyof typeof notifications] })}
              />
            </div>
          ))}
        </div>
      </Card>

      {/* API Credentials Section */}
      <Card variant="gradient">
        <CardHeader>
          <div className="flex items-center gap-3">
            <div className="p-2 rounded-xl bg-gradient-to-br from-purple-500 to-purple-600">
              <KeyIcon className="h-5 w-5 text-white" />
            </div>
            <CardTitle>API Credentials</CardTitle>
          </div>
        </CardHeader>
        <div className="space-y-5">
          <Input
            label="Copernicus Username"
            placeholder="Enter Copernicus username"
          />
          <Input
            label="Copernicus Password"
            type="password"
            placeholder="Enter Copernicus password"
          />
          <div className="p-4 rounded-xl bg-gradient-to-br from-blue-50 to-indigo-50 border border-blue-100">
            <p className="text-sm text-blue-700">
              Register for free at{' '}
              <a
                href="https://scihub.copernicus.eu/dhus/#/self-registration"
                target="_blank"
                rel="noopener noreferrer"
                className="font-semibold underline hover:text-blue-800"
              >
                Copernicus Open Access Hub
              </a>
              {' '}to access satellite imagery.
            </p>
          </div>
        </div>
      </Card>

      {/* Data Preferences Section */}
      <Card variant="gradient">
        <CardHeader>
          <div className="flex items-center gap-3">
            <div className="p-2 rounded-xl bg-gradient-to-br from-amber-500 to-amber-600">
              <Cog6ToothIcon className="h-5 w-5 text-white" />
            </div>
            <CardTitle>Data Preferences</CardTitle>
          </div>
        </CardHeader>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-5">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1.5">
              Satellite Source
            </label>
            <select
              value={preferences.satellite}
              onChange={(e) => setPreferences({ ...preferences, satellite: e.target.value })}
              className="w-full px-4 py-3 bg-white border border-gray-200 rounded-xl text-gray-900 focus:border-kenagri-500 focus:ring-2 focus:ring-kenagri-500/20 transition-all"
            >
              <option value="sentinel-2">Sentinel-2 (10m)</option>
              <option value="landsat-8">Landsat 8 (30m)</option>
            </select>
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1.5">
              Max Cloud Cover
            </label>
            <select
              value={preferences.maxCloudCover}
              onChange={(e) => setPreferences({ ...preferences, maxCloudCover: parseInt(e.target.value) })}
              className="w-full px-4 py-3 bg-white border border-gray-200 rounded-xl text-gray-900 focus:border-kenagri-500 focus:ring-2 focus:ring-kenagri-500/20 transition-all"
            >
              <option value={10}>10%</option>
              <option value={20}>20%</option>
              <option value={30}>30%</option>
              <option value={50}>50%</option>
            </select>
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1.5">
              Units
            </label>
            <select
              value={preferences.units}
              onChange={(e) => setPreferences({ ...preferences, units: e.target.value })}
              className="w-full px-4 py-3 bg-white border border-gray-200 rounded-xl text-gray-900 focus:border-kenagri-500 focus:ring-2 focus:ring-kenagri-500/20 transition-all"
            >
              <option value="metric">Metric (ha, °C)</option>
              <option value="imperial">Imperial (ac, °F)</option>
            </select>
          </div>
        </div>
      </Card>

      {/* Save Button */}
      <div className="flex justify-end pt-4">
        <Button
          variant="primary"
          size="lg"
          onClick={handleSave}
          leftIcon={<CheckIcon className="h-5 w-5" />}
        >
          Save Settings
        </Button>
      </div>
    </div>
  );
}
